import {
  Box,
  Button,
  ButtonGroup,
  Link,
  ListItem,
  Popover,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import {
  IconCloudDownload,
  IconCode,
  IconFileAlert,
  IconLock,
  IconRepeat,
  IconTrashX,
} from '@tabler/icons-react';
import { useUsuario } from '../../../../providers/usuarioProvider';
import { updateDoc, deleteDoc } from 'firebase/firestore';
import UploadDb from '../../../../db/uploadDb';
import Upload from '../../../../models/upload';
import { SituacaoUpload, TipoUpload } from '../../../../models/enum';
import Functions from '../../../../callable/functions';
import ChipTipoUpload from '../../../util/ui/chipTipoUpload';
import ChipExtensao from '../../../util/ui/chipExtensao';
import { getDownloadURL, getStorage, ref } from 'firebase/storage';

type Props = {
  upload: Upload;
};

/** Link em accent-ink: `primary.main` reprova em contraste sobre o dark. */
const sxLink = (theme: Theme) => ({
  color: theme.palette.accentInk,
  textDecorationColor: theme.palette.accentInk,
});

export default function UploadComErro({ upload }: Props) {
  const { visualizado } = useUsuario();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [senha, setSenha] = useState('');
  const [avisoSenha, setAvisoSenha] = useState<string>();

  return (
    <ListItem
      disableGutters
      sx={(theme) => ({
        px: 2,
        py: 1,
        // Linha sinalizada: hairline de 2px em destructive à esquerda,
        // mesmo padrão da linha selecionada em tabela (DESIGN.md → Tabelas).
        borderLeft: `2px solid ${theme.palette.error.main}`,
      })}
    >
      <Box display="flex" flexDirection="column" style={{ width: '100%' }}>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              color: 'error.main',
              flexShrink: 0,
            }}
          >
            <IconFileAlert size={16} />
          </Box>
          <Tooltip title={upload.nomeArquivoOriginal || ''} placement="top">
            <Box
              display="flex"
              flexGrow={1}
              minWidth={0}
              px={1}
              alignItems="center"
              gap={1}
            >
              <Typography
                variant="body1"
                component="div"
                color="error.main"
                noWrap
                sx={{ minWidth: 0 }}
              >
                Erro ao importar{' '}
                <Typography variant="dataMono" component="span">
                  {upload.nomeArquivoOriginal}
                </Typography>
              </Typography>
              {upload.erro && (
                <Popover
                  open={!!anchorEl}
                  anchorEl={anchorEl}
                  onClose={() => setAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={0.5}
                    p={1.5}
                    maxWidth={420}
                  >
                    <Typography
                      variant="dataMono"
                      sx={{ fontSize: 12 }}
                    >{`Erro: ${upload.erro.codErro}`}</Typography>
                    {upload.erro.detalhes && (
                      <Typography
                        variant="dataMono"
                        color="text.secondary"
                        sx={{ fontSize: 12, wordBreak: 'break-word' }}
                      >{`Detalhes: ${upload.erro.detalhes}`}</Typography>
                    )}
                  </Box>
                </Popover>
              )}
            </Box>
          </Tooltip>
          <Box display="flex" gap={0.5} alignItems="center" mr={1}>
            {upload.senhaProtegida && (
              <Tooltip title="PDF protegido por senha" placement="top">
                <Box
                  display="inline-flex"
                  alignItems="center"
                  color="text.secondary"
                >
                  <IconLock size={16} />
                </Box>
              </Tooltip>
            )}
            <ChipTipoUpload tipo={upload.tipo} />
            <ChipExtensao nomeArquivo={upload.nomeArquivoOriginal} />
          </Box>
          <ButtonGroup size="small" variant="outlined">
            {upload.erro && (
              <Button
                size="small"
                color="info"
                onClick={async () => {
                  if (!!visualizado) {
                    console.log('updateDoc');
                    await updateDoc(
                      new UploadDb().doc({
                        usuarioId: visualizado,
                        uploadId: upload.id,
                      }),
                      { situacao: SituacaoUpload.SALVO },
                    );
                    await new Functions().relerUpload({ uploadId: upload.id });
                  }
                }}
              >
                <IconRepeat size={16} />
              </Button>
            )}
            {upload.erro && (
              <Button
                size="small"
                color="info"
                onClick={(event) => setAnchorEl(event.currentTarget)}
              >
                <IconCode size={16} />
              </Button>
            )}
            <Button
              size="small"
              color="info"
              onClick={async () => {
                if (upload.arquivo) {
                  const url = await getDownloadURL(
                    ref(getStorage(), upload.arquivo),
                  );
                  window.open(url, '_blank');
                }
              }}
            >
              <IconCloudDownload size={16} />
            </Button>
            <Button
              size="small"
              color="error"
              onClick={async () => {
                if (!!visualizado)
                  await deleteDoc(
                    new UploadDb().doc({
                      usuarioId: visualizado,
                      uploadId: upload.id,
                    }),
                  );
              }}
            >
              <IconTrashX size={16} />
            </Button>
          </ButtonGroup>
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          width="100%"
          justifyContent="space-between"
          alignItems="flex-start"
          py={1}
          pl={3}
        >
          {(function () {
            if (upload.erro) {
              if (upload.tipo === TipoUpload.CEI) {
                return erroCei();
              } else {
                return erroPdf();
              }
            }
          })()}
        </Box>
      </Box>
    </ListItem>
  );

  async function enviarSenha() {
    if (!senha) return;
    try {
      const { data } = await new Functions().definirSenhaPdf({
        uploadId: upload.id,
        senha,
      });
      if (data.jaCadastrada) {
        setAvisoSenha(
          'Essa senha já foi cadastrada. Se o erro persistir, o problema provavelmente não é a senha.',
        );
      } else {
        setAvisoSenha(undefined);
        setSenha('');
      }
    } catch (error) {
      console.error(error);
      setAvisoSenha('Não foi possível salvar a senha. Tente novamente.');
    }
  }

  function erroPdf() {
    switch (upload.erro.codErro) {
      case 6:
        return upload.erro.detalhes &&
          upload.erro.detalhes.includes('TERMO') ? (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Por enquanto, não somos compatíveis com operações no Mercado a
            Termo, por isso este upload não foi importado.
          </Typography>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Não identifiquei alguma operação deste arquivo. Envie um email para
            suporte@felixnabolsa.com.br que vamos te ajudar.
          </Typography>
        );
      case 9:
        // codErro 9 também cobre "não legível" genérico; só é senha quando o
        // backend marcou senhaProtegida (ou seja, tentou o desbloqueio).
        if (!upload.senhaProtegida) {
          return (
            <Typography variant="body2" color="text.secondary">
              Não foi possível ler este arquivo. Ele pode estar corrompido,
              incompleto ou em um formato não suportado. Baixe a nota original
              no site ou app da corretora e refaça o upload.
            </Typography>
          );
        }
        return (
          <Box
            display="flex"
            flexDirection="column"
            gap={1.5}
            width="100%"
            maxWidth={560}
          >
            <Typography variant="body2" color="text.secondary">
              Este PDF está protegido por senha. Informe a senha para importar a
              nota.
            </Typography>

            <Box display="flex" gap={1} alignItems="center">
              <TextField
                size="small"
                type="password"
                placeholder="Senha do PDF"
                autoComplete="off"
                value={senha}
                onChange={(e) => {
                  setSenha(e.target.value);
                  setAvisoSenha(undefined);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') enviarSenha();
                }}
                sx={{ flexGrow: 1, minWidth: 0 }}
              />
              <Button
                size="small"
                variant="contained"
                disabled={!senha}
                onClick={() => enviarSenha()}
                sx={{ flexShrink: 0 }}
              >
                Usar senha
              </Button>
            </Box>

            {avisoSenha && (
              <Typography variant="body2" color="warning.main">
                {avisoSenha}
              </Typography>
            )}

            {upload.erro?.senhasTentadas &&
              (!!upload.erro.senhasTentadas.cpf?.length ||
                !!upload.erro.senhasTentadas.cadastradas?.length) && (
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={0.5}
                  sx={{
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    pt: 1,
                  }}
                >
                  <Typography variant="labelMono" color="text.disabled">
                    Senhas já testadas
                  </Typography>
                  {!!upload.erro.senhasTentadas.cpf?.length && (
                    <Typography variant="body2" color="text.secondary">
                      Inferidas do seu CPF:{' '}
                      <Typography
                        variant="dataMono"
                        component="span"
                        sx={{ fontSize: 12 }}
                      >
                        {upload.erro.senhasTentadas.cpf.join(' · ')}
                      </Typography>
                    </Typography>
                  )}
                  {!!upload.erro.senhasTentadas.cadastradas?.length && (
                    <Typography variant="body2" color="text.secondary">
                      Salvas por você em outras notas:{' '}
                      <Typography
                        variant="dataMono"
                        component="span"
                        sx={{ fontSize: 12 }}
                      >
                        {upload.erro.senhasTentadas.cadastradas.join(' · ')}
                      </Typography>
                    </Typography>
                  )}
                </Box>
              )}
          </Box>
        );
      case 10:
        return (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Este upload não é compatível com nosso leitor automático. Envie um
            email para suporte@felixnabolsa.com.br que vamos te ajudar.
          </Typography>
        );
      case 14:
        return (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Algum ativo não foi identificado (
            <Typography variant="dataMono" component="span" sx={{ fontSize: 12 }}>
              {upload.erro.detalhes}
            </Typography>
            ). Envie um
            email para suporte@felixnabolsa.com.br que vamos te ajudar.
          </Typography>
        );
      case 15:
        return (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Algumas taxas não foram encontradas. Envie um email para
            suporte@felixnabolsa.com.br que vamos te ajudar.
          </Typography>
        );
      case 17:
        return (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Não foram encontradas operações em algumas notas. Envie um email
            para suporte@felixnabolsa.com.br que vamos te ajudar.
          </Typography>
        );
      case 19:
        return (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            O padrão do upload não foi reconhecido, certifique-se de que as
            notas estão no padrão Sinacor.
          </Typography>
        );
      case 23:
        return (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Este upload possui nota(s) já cadastrada(s).
          </Typography>
        );
      case 27:
        return (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Este upload pertence a um CPF diferente do CPF que está cadastrado
            no usuário. Envie um email para suporte@felixnabolsa.com.br que
            vamos te ajudar.
          </Typography>
        );
      case 37:
        return (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Algumas operações ainda não são compatíveis com as conexões
            automáticas, basta cadastrá-las manualmente: Arquivo {'>'} Notas{' '}
            {'>'} + Nota manual. (
            <Typography variant="dataMono" component="span" sx={{ fontSize: 12 }}>
              {upload.erro.detalhes}
            </Typography>
            ).
          </Typography>
        );
      case 50:
        return (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Não foi possível ler o conteúdo deste PDF. Ele é uma imagem/scan ou
            foi re-impresso/re-gerado (ex.: "Microsoft Print to PDF", ou "Salvar
            como PDF" a partir de um visualizador), o que apaga a camada de
            texto pesquisável. Baixe a nota original direto no site ou app da
            corretora e tente novamente.
          </Typography>
        );
      case 51:
        return (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Este arquivo está corrompido ou incompleto (pode ter sido um download
            interrompido) ou foi salvo de forma incorreta (ex.: "Imprimir como
            PDF" a partir de um visualizador). Baixe a nota original no site ou
            app da corretora e tente novamente.
          </Typography>
        );
      default:
        return (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Você já tentou repetir a importação do upload clicando neste
            primeiro botão aqui à direita?
          </Typography>
        );
    }
  }

  function erroCei() {
    switch (upload.erro.codErro) {
      case 17:
        return (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            No{' '}
            <Link
              href="https://ceiapp.b3.com.br"
              target="_blank"
              sx={sxLink}
            >
              CEI
            </Link>
            , seguiu os passos: Extratos e Informativos {'>'} Negociação de
            Ativos {'>'} Exportar para Excel?
          </Typography>
        );
      case 23:
        return (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Este arquivo possui alguma(s) nota(s) já cadastrada(s).
          </Typography>
        );
      default:
        return (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Já deu uma olhada na parte de "Cadastro de notas" das{' '}
            <Link
              href="https://felixnabolsa.gitbook.io/felix-na-bolsa/notas-de-corretagem/cadastro-de-notas"
              target="_blank"
              sx={sxLink}
            >
              docs
            </Link>
            ?
          </Typography>
        );
    }
  }
}
